import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import SAT_HDS from "../config/satHds";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * CIRCLE OF LIFE™ — public landing page.
 * SEO-fähig. Zeigt Methodik + hochwertige Preview. Kein Assessment startbar hier.
 * CTA öffnet Experience nur bei aktiver Berechtigung; sonst Locked-Screen /
 * Access-Request-Flow.
 */

// Life Wheel SVG — Beispiel-Grafik mit stilisierten Dimensionen
const LifeWheelPreview = () => {
  const dims = SAT_HDS.experiences.circle.dimensions;
  const n = dims.length;
  const cx = 240;
  const cy = 240;
  const rOuter = 200;
  // sample "reality" and "desire" values (illustrative only, marked "BEISPIEL")
  const sample = [72, 55, 68, 60, 45, 70, 82, 48, 63, 55, 78, 66];
  const desire = [88, 82, 78, 90, 85, 92, 95, 88, 94, 80, 92, 90];

  const pointsFor = (values, scale) =>
    values
      .map((v, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const r = (v / 100) * rOuter * scale;
        return `${(cx + Math.cos(a) * r).toFixed(1)},${(cy + Math.sin(a) * r).toFixed(1)}`;
      })
      .join(" ");

  return (
    <svg viewBox="0 0 480 480" className="w-full max-w-lg mx-auto" aria-hidden="true">
      <defs>
        <radialGradient id="wheelGlow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={rOuter + 10} fill="url(#wheelGlow)" />
      {/* concentric */}
      {[0.25, 0.5, 0.75, 1].map((s) => (
        <circle key={s} cx={cx} cy={cy} r={rOuter * s} fill="none" stroke="#D4AF37" strokeWidth="0.6" opacity="0.28" />
      ))}
      {/* spokes + labels */}
      {dims.map((d, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const lx = cx + Math.cos(a) * (rOuter + 24);
        const ly = cy + Math.sin(a) * (rOuter + 24);
        const anchor = Math.cos(a) > 0.15 ? "start" : Math.cos(a) < -0.15 ? "end" : "middle";
        return (
          <g key={d.key}>
            <line
              x1={cx}
              y1={cy}
              x2={cx + Math.cos(a) * rOuter}
              y2={cy + Math.sin(a) * rOuter}
              stroke="#D4AF37"
              strokeWidth="0.4"
              opacity="0.35"
            />
            <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" fontSize="10" fill="#F6E7B8" opacity="0.85" fontFamily="serif">
              {d.label}
            </text>
          </g>
        );
      })}
      {/* desired */}
      <polygon points={pointsFor(desire, 1)} fill="#7C6FE0" fillOpacity="0.12" stroke="#7C6FE0" strokeWidth="1" strokeDasharray="4 3" />
      {/* reality */}
      <polygon points={pointsFor(sample, 1)} fill="#D4AF37" fillOpacity="0.22" stroke="#D4AF37" strokeWidth="1.4" />
      {/* dots */}
      {sample.map((v, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const r = (v / 100) * rOuter;
        return <circle key={i} cx={cx + Math.cos(a) * r} cy={cy + Math.sin(a) * r} r="2.5" fill="#F6E7B8" />;
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="9" fill="#D4AF37" opacity="0.7" letterSpacing="4">BEISPIEL</text>
    </svg>
  );
};

const CircleOfLifeLanding = () => {
  const cfg = SAT_HDS.experiences.circle;
  const [entitlement, setEntitlement] = useState(null);

  useEffect(() => {
    // Best effort — public users get "locked"
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/api/self-discovery/me/entitlements`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const item = (res.data.items || []).find((e) => e.assessment_slug === cfg.key);
        setEntitlement(item || null);
      } catch (_) {
        setEntitlement(null);
      }
    })();
  }, [cfg.key]);

  const isActive = entitlement?.status === "active";
  const isRequested = entitlement?.status === "requested";

  return (
    <div data-testid="circle-of-life-landing" className="bg-[#08102A] text-white min-h-screen">
      <SEO
        title="Circle of Life™ · Die Landkarte deines heutigen Lebens"
        description="Circle of Life ist eine exklusive SAT Experience. Betrachte dein Leben als System und mache Ressourcen, Spannungsfelder und dein Transformationspotenzial sichtbar."
        path={SAT_HDS.paths.circle}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-24 sm:pt-32 pb-20">
        <div className="pointer-events-none absolute inset-0"
             style={{ background: "radial-gradient(900px 420px at 50% 0%, rgba(212,175,55,0.14), transparent 70%)" }} />
        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <div className="text-[11px] tracking-[0.4em] text-[#D4AF37] uppercase mb-6">SAT SELF DISCOVERY · 01</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#F6E7B8]">
            {cfg.trademark}
          </h1>
          <p className="mt-6 text-xl sm:text-2xl italic text-white/70">{cfg.hero}</p>
          <p className="mt-10 text-base sm:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
            {cfg.intro}
          </p>

          <div className="mt-12">
            {isActive ? (
              <button
                data-testid="cta-open-experience"
                onClick={() => alert("Assessment startet in Milestone 2")}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-[#08102A] font-medium tracking-wider hover:bg-[#F6E7B8] transition"
              >
                {cfg.ctaSecondary}
              </button>
            ) : isRequested ? (
              <div className="inline-block px-6 py-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] tracking-widest text-sm">
                ZUGANG ANGEFRAGT · SAT PRÜFT
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/erstgespraech"
                  data-testid="cta-request-access"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-[#08102A] font-medium tracking-wider hover:bg-[#F6E7B8] transition"
                >
                  ZUGANG ANFRAGEN
                </Link>
                <Link
                  to="/ecosystem"
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] tracking-widest text-sm hover:border-[#D4AF37] transition"
                >
                  SAT PROGRAMME ENTDECKEN
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* METHODIK */}
      <section className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-5xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-4">DIE METHODIK</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F6E7B8] leading-tight">
              Drei Perspektiven auf jeden Lebensbereich.
            </h2>
            <div className="mt-8 space-y-6 text-white/80">
              <div className="flex items-start gap-4">
                <div className="mt-1 w-10 h-10 rounded-full border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] font-serif text-lg">R</div>
                <div>
                  <div className="text-[#F6E7B8] font-medium">Reality</div>
                  <p className="text-sm text-white/65 mt-1">Wo stehst du heute? Ehrliche Momentaufnahme, ohne Beschönigung.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 w-10 h-10 rounded-full border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] font-serif text-lg">D</div>
                <div>
                  <div className="text-[#F6E7B8] font-medium">Desire</div>
                  <p className="text-sm text-white/65 mt-1">Wo möchtest du sein? Der Zustand, der dich nährt und weiterbringt.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 w-10 h-10 rounded-full border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] font-serif text-lg">I</div>
                <div>
                  <div className="text-[#F6E7B8] font-medium">Importance</div>
                  <p className="text-sm text-white/65 mt-1">Wie wichtig ist dieser Bereich für dich? Nicht jede Lücke braucht denselben Fokus.</p>
                </div>
              </div>
            </div>
            <div className="mt-10 text-xs tracking-[0.3em] uppercase text-[#D4AF37]/80">
              REALITY × DESIRE × IMPORTANCE
            </div>
          </div>

          <div className="rounded-2xl border border-[#D4AF37]/15 bg-white/[0.02] p-6 sm:p-10">
            <LifeWheelPreview />
            <div className="mt-4 flex justify-center gap-6 text-xs text-white/60">
              <span className="flex items-center gap-2"><span className="inline-block w-3 h-1 bg-[#D4AF37]" />Reality</span>
              <span className="flex items-center gap-2"><span className="inline-block w-3 h-1 bg-[#7C6FE0]" />Desire</span>
            </div>
          </div>
        </div>
      </section>

      {/* DIMENSIONS */}
      <section className="py-20 bg-[#0B1330] border-t border-white/5">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-4">LEBENSDIMENSIONEN</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#F6E7B8] leading-tight max-w-2xl">
            Zwölf Lebensbereiche. Ein zusammenhängendes System.
          </h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {cfg.dimensions.map((d) => (
              <div key={d.key} className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/80 hover:border-[#D4AF37]/40 transition">
                {d.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-4">WAS DU ERHÄLTST</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#F6E7B8] leading-tight">
            Deine persönliche Standortbestimmung.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              ["Life Alignment Score", "Ein Gesamtwert deiner heutigen inneren Ausrichtung — 0 bis 100."],
              ["Ressourcen & Spannungsfelder", "Welche Bereiche tragen dich, welche verlangen Bewusstheit."],
              ["Transformation Gap™", "Wo zwischen deiner Realität und deinem Wunschzustand der größte Entwicklungsraum liegt."],
              ["Reflexionsfragen", "Persönliche Fragen, die zur Wahrheit einladen — nicht zur Diagnose."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl border border-[#D4AF37]/15 bg-white/[0.02] p-6">
                <div className="text-[#F6E7B8] font-medium">{t}</div>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 border-t border-white/5 bg-gradient-to-b from-[#08102A] to-[#050914]">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="font-serif text-2xl sm:text-3xl text-[#F6E7B8] italic leading-snug">
            „{cfg.centralMessage}“
          </p>
          <div className="mt-10">
            {!isActive && !isRequested && (
              <Link
                to="/erstgespraech"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-[#08102A] font-medium tracking-wider hover:bg-[#F6E7B8] transition"
              >
                {cfg.cta}
              </Link>
            )}
          </div>
          <p className="mt-8 text-xs text-white/40 tracking-widest uppercase">
            Diese Experience ist eine persönliche Reflexion — keine medizinische oder psychologische Diagnose.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CircleOfLifeLanding;
